import React from "react";
import { over } from "lodash/fp";
import cn from "classnames";
import authenticationStarted, {
  AuthType,
} from "storefront/Analytics/EventCreators/authenticationStarted";
import { IdentityCredentials } from "storefront/GrailedAPI/v1/Sessions/create";
import useAnalytics from "storefront/hooks/useAnalytics";
import { Tracker } from "storefront/Analytics/Tracker";
import Button from "storefront/components/Button";
import { completeIdentityProviderEvent } from "../CompleteIdentityProviderEvent";
import { failIdentityProviderEvent } from "../FailIdentityProviderEvent";
import { FacebookIdentityProviderError } from "./Error";
import useFacebookLogin from "./useFacebookLogin";
// eslint-disable-next-line css-modules/no-unused-class
import styles from "../../../Button.module.scss";

type Props = {
  onSuccess: (credentials: IdentityCredentials) => unknown;
  onError: (error: FacebookIdentityProviderError) => void;
  children: React.ReactNode;
  className?: string;
  authType: AuthType;
};

const withTracking =
  (track: Tracker["track"], authType: AuthType) =>
  (f: (event: React.MouseEvent<HTMLButtonElement>) => void) =>
    over([() => track(authenticationStarted("facebook", authType)), f]);

const Facebook = ({
  onSuccess,
  onError,
  children,
  className,
  authType,
}: Props) => {
  const { track } = useAnalytics();
  const method = authType === "signup" ? "CreateAccount" : "LogIn";

  const onFacebookLoginSuccess = (credentials: IdentityCredentials) => {
    track(completeIdentityProviderEvent(method, "facebook"));
    onSuccess(credentials);
  };

  const onFacebookLoginFailure = (error: FacebookIdentityProviderError) => {
    track(failIdentityProviderEvent(method, error));
    onError(error);
  };

  const [attemptLogin, isProcessing] = useFacebookLogin({
    onSuccess: onFacebookLoginSuccess,
    onFailure: onFacebookLoginFailure,
  });

  const onClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    attemptLogin();
  };

  return (
    <Button
      type="button"
      size="large"
      variant="secondary"
      className={cn(styles.authButton, styles.facebook, className)}
      onClick={withTracking(track, authType)(onClick)}
      disabled={isProcessing}
    >
      {children}
    </Button>
  );
};

export default Facebook;
